import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';

const PublicInsightForm = ({ onBack, navigate }) => {
    const [counselors, setCounselors] = useState([]);
    const [loadingCounselors, setLoadingCounselors] = useState(true);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        counselorId: '',
        notes: ''
    });
    
    const [showPreview, setShowPreview] = useState(false);
    const [status, setStatus] = useState('idle'); // 'idle', 'submitting', 'success', 'error'
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const fetchCounselors = async () => {
            try {
                const data = await DataManager.getAllCounselors();
                setCounselors(data);

                // Auto-select counselor from query param ?counselor=...
                const urlParams = new URLSearchParams(window.location.search);
                const counselorParam = urlParams.get('counselor');
                if (counselorParam && data && data.length > 0) {
                    const matched = data.find(c => 
                        c.id === counselorParam || 
                        c.email?.toLowerCase() === counselorParam.toLowerCase()
                    );
                    if (matched) {
                        setFormData(prev => ({ ...prev, counselorId: matched.id }));
                    }
                }
            } catch (error) {
                console.error("Error fetching counselors:", error);
            } finally {
                setLoadingCounselors(false);
            }
        };
        
        fetchCounselors();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setErrorMessage("Per favore, inserisci il tuo Nome e Cognome.");
            return false;
        }
        if (!formData.email.trim()) {
            setErrorMessage("Per favore, inserisci il tuo indirizzo email.");
            return false;
        }
        if (!formData.counselorId) {
            setErrorMessage("Seleziona il counselor a cui inviare l'insight.");
            return false;
        }
        if (!formData.notes.trim()) {
            setErrorMessage("Per favore, scrivi la tua riflessione o insight prima di continuare.");
            return false;
        }
        setErrorMessage('');
        return true;
    };

    const handleOpenPreview = (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowPreview(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        if (!validateForm()) {
            setStatus('error');
            return;
        }

        setStatus('submitting');
        setErrorMessage('');

        try {
            await DataManager.savePublicInsight({
                name: formData.name.trim(),
                email: formData.email.trim().toLowerCase(),
                counselorId: formData.counselorId,
                notes: formData.notes.trim(),
                submittedAt: new Date().toISOString()
            });
            
            setStatus('success');
            setShowPreview(false);
            setFormData({ name: '', email: '', counselorId: '', notes: '' });
        } catch (error) {
            console.error("Error saving insight:", error);
            setErrorMessage("Si è verificato un errore durante l'invio. Riprova più tardi.");
            setStatus('error');
        }
    };

    const selectedCounselorObj = counselors.find(c => c.id === formData.counselorId);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-brand-50/40 to-slate-200 flex flex-col items-center p-4 sm:p-10 font-sans text-slate-900">
            
            <div className="w-full max-w-2xl flex justify-between items-center mb-6">
                <button 
                    onClick={() => onBack ? onBack() : navigate('/')}
                    className="text-slate-700 hover:text-slate-950 font-bold text-sm flex items-center gap-2 bg-white/80 hover:bg-white px-4 py-2 rounded-xl border border-slate-300 shadow-xs transition-all"
                >
                    <span>←</span> Torna indietro
                </button>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 bg-white/80 px-3 py-1.5 rounded-full border border-slate-200">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    Modulo Sicuro Crittografato
                </div>
            </div>

            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-10 max-w-2xl w-full border border-slate-200">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-brand-100 text-brand-700 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-3 shadow-inner">
                        💡
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mb-2">Condividi un Insight</h1>
                    <p className="text-slate-600 text-sm leading-relaxed max-w-lg mx-auto">
                        Invia una riflessione, un sogno, un'intuizione o una nota al tuo counselor tra una sessione e l'altra, senza bisogno di effettuare il login.
                    </p>
                </div>

                {status === 'success' ? (
                    <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-8 text-center space-y-4">
                        <span className="text-5xl block">✨</span>
                        <h3 className="text-2xl font-black text-emerald-900">Insight Inviato con Successo!</h3>
                        <p className="text-emerald-800 text-sm leading-relaxed max-w-md mx-auto">
                            Il tuo messaggio è stato recapitato nella cartella protetta del counselor. Verrà integrato nel tuo percorso di consapevolezza.
                        </p>
                        <div className="pt-4">
                            <button 
                                onClick={() => setStatus('idle')}
                                className="px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm rounded-xl shadow-md transition-all active:scale-95"
                            >
                                Invia un altro insight
                            </button>
                        </div>
                    </div>
                ) : showPreview ? (
                    /* ANTEPRIMA DEL CLIENTE PRIMA DELL'INVIO */
                    <div className="space-y-6 animate-fade-in">
                        <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-2xl flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">👁️</span>
                                <div>
                                    <h4 className="font-bold text-amber-950 text-sm">Anteprima del tuo Insight</h4>
                                    <p className="text-amber-800 text-xs">Rivedi i dati prima dell'invio definitivo al counselor.</p>
                                </div>
                            </div>
                            <span className="px-2.5 py-1 bg-amber-200 text-amber-900 font-black text-[11px] uppercase rounded-full">
                                Bozza
                            </span>
                        </div>

                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4 border-b border-slate-200">
                                <div>
                                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Autore / Nome</span>
                                    <p className="text-slate-900 font-bold text-base mt-0.5">{formData.name}</p>
                                </div>
                                <div>
                                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Email di riferimento</span>
                                    <p className="text-slate-900 font-bold text-base mt-0.5">{formData.email}</p>
                                </div>
                            </div>

                            <div className="pb-4 border-b border-slate-200">
                                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">Counselor destinatario</span>
                                <p className="text-brand-800 font-bold text-base mt-0.5 flex items-center gap-2">
                                    <span>👤</span> {selectedCounselorObj ? (selectedCounselorObj.name || selectedCounselorObj.email) : 'Selezionato'}
                                </p>
                            </div>

                            <div>
                                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-2">Testo dell'Insight / Note</span>
                                <div className="p-4 bg-white rounded-xl border border-slate-200 text-slate-800 text-sm leading-relaxed whitespace-pre-wrap font-medium shadow-2xs">
                                    {formData.notes}
                                </div>
                            </div>
                        </div>

                        {status === 'error' && (
                            <div className="bg-rose-50 border border-rose-300 text-rose-800 p-4 rounded-xl text-sm font-semibold flex items-center gap-2">
                                <span>⚠️</span> {errorMessage}
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <button 
                                type="button"
                                onClick={() => setShowPreview(false)}
                                className="flex-1 py-3.5 px-5 rounded-xl font-bold text-sm text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 transition-all text-center"
                            >
                                ✏️ Modifica Testo
                            </button>
                            <button 
                                type="button"
                                disabled={status === 'submitting'}
                                onClick={handleSubmit}
                                className={`flex-1 py-3.5 px-5 rounded-xl font-bold text-sm text-white shadow-lg shadow-brand-600/30 transition-all text-center
                                    ${status === 'submitting' 
                                        ? 'bg-brand-400 cursor-not-allowed' 
                                        : 'bg-brand-600 hover:bg-brand-700 active:scale-98'
                                    }
                                `}
                            >
                                {status === 'submitting' ? 'Invio in corso...' : '✓ Conferma e Invia al Counselor'}
                            </button>
                        </div>
                    </div>
                ) : (
                    /* FORM DI COMPILAZIONE */
                    <form onSubmit={handleOpenPreview} className="space-y-5">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                Il tuo Nome e Cognome <span className="text-rose-500">*</span>
                            </label>
                            <input 
                                type="text"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Es. Mario Rossi"
                                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all outline-none bg-slate-50/50 focus:bg-white text-sm font-medium"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">
                                Indica come ti chiami così il counselor saprà subito chi ha scritto la riflessione.
                            </p>
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                La tua Email <span className="text-rose-500">*</span>
                            </label>
                            <input 
                                type="email"
                                name="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Es. mario.rossi@email.com"
                                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all outline-none bg-slate-50/50 focus:bg-white text-sm font-medium"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">
                                Servirà per collegare in automatico la nota alla tua scheda personale.
                            </p>
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                Seleziona il tuo Counselor <span className="text-rose-500">*</span>
                            </label>
                            {loadingCounselors ? (
                                <div className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-100 text-slate-500 text-sm font-medium animate-pulse">
                                    Caricamento elenco counselor...
                                </div>
                            ) : (
                                <select
                                    name="counselorId"
                                    required
                                    value={formData.counselorId}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all outline-none bg-slate-50/50 focus:bg-white text-sm font-bold"
                                >
                                    <option value="" disabled>-- Seleziona il counselor di riferimento --</option>
                                    {counselors.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.name ? `${c.name} (${c.email})` : c.email}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                Le tue Riflessioni o Insight <span className="text-rose-500">*</span>
                            </label>
                            <textarea 
                                name="notes"
                                required
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder="Scrivi liberamente qui i tuoi pensieri, un sogno, un'intuizione emotiva o qualcosa emerso dopo l'ultimo incontro..."
                                rows="6"
                                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all outline-none resize-y text-sm leading-relaxed bg-slate-50/50 focus:bg-white font-medium"
                            ></textarea>
                        </div>

                        {errorMessage && (
                            <div className="bg-rose-50 border border-rose-300 text-rose-800 p-4 rounded-xl text-sm font-semibold flex items-center gap-2">
                                <span>⚠️</span> {errorMessage}
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 pt-3">
                            <button 
                                type="submit"
                                disabled={loadingCounselors}
                                className="flex-1 py-3.5 px-6 rounded-xl font-black text-sm text-brand-900 bg-brand-100 hover:bg-brand-200 border border-brand-300 transition-all flex items-center justify-center gap-2 shadow-xs active:scale-98"
                            >
                                <span>👁️</span> Visualizza Anteprima
                            </button>
                            <button 
                                type="button"
                                onClick={handleSubmit}
                                disabled={status === 'submitting' || loadingCounselors}
                                className={`flex-1 py-3.5 px-6 rounded-xl font-black text-sm text-white shadow-lg shadow-brand-600/30 transition-all flex items-center justify-center gap-2 active:scale-98
                                    ${status === 'submitting' || loadingCounselors 
                                        ? 'bg-brand-400 cursor-not-allowed' 
                                        : 'bg-brand-600 hover:bg-brand-700 hover:shadow-brand-600/40'
                                    }
                                `}
                            >
                                <span>🚀</span> {status === 'submitting' ? 'Invio in corso...' : 'Invia Subito'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default PublicInsightForm;
